/**
 * Centralized HTTP client for API calls.
 */

export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
}

export interface ApiResponse<T> {
  data: T
  pagination?: {
    page: number
    per_page: number
    total: number
    pages: number
  }
}

const TOKEN_KEY = 'auth_token'

/** The shipped base URL: same-origin, proxied to the API by nginx / vite. */
export const DEFAULT_API_BASE_URL = '/api/v1'

/**
 * The global an out-of-browser host may set to point the singleton client at an
 * **absolute** base URL.
 *
 * The only consumer today is a DOM test that serves the API from a real local
 * `http.createServer` (a relative path has no origin to resolve against outside
 * a browser). Nothing in the app, the build, or the container sets it, so the
 * shipped behaviour — `/api/v1`, same origin — is unchanged.
 */
export const API_BASE_URL_GLOBAL = '__API_BASE_URL__'

function injectedBaseUrl(): string | null {
  const value = (globalThis as Record<string, unknown>)[API_BASE_URL_GLOBAL]
  return typeof value === 'string' && value.length > 0 ? value : null
}

class ApiClient {
  /** An explicitly constructed base URL always wins; null means "resolve it". */
  private readonly explicitBaseUrl: string | null

  constructor(baseUrl?: string) {
    this.explicitBaseUrl = baseUrl ?? null
  }

  /**
   * Resolved per request, not frozen at construction: the client is a module
   * singleton created at import time, so an injected base URL would otherwise
   * depend on import order.
   */
  private get baseUrl(): string {
    return this.explicitBaseUrl ?? injectedBaseUrl() ?? DEFAULT_API_BASE_URL
  }

  private getAuthToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  }

  private buildUrl(path: string, params?: Record<string, string | number>): string {
    let url = `${this.baseUrl}${path}`
    if (params) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, String(value))
      })
      url += `?${searchParams.toString()}`
    }
    return url
  }

  private buildHeaders(extraHeaders?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Add auth token if available
    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    // Per-request headers (e.g. X-Application-ID tenant context) are merged last so
    // a caller can add context without the client guessing a global default.
    Object.assign(headers, extraHeaders ?? {})
    return headers
  }

  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown
      params?: Record<string, string | number>
      headers?: Record<string, string>
    } = {}
  ): Promise<ApiResponse<T>> {
    const { body, params, headers: extraHeaders } = options

    const url = this.buildUrl(path, params)
    const headers = this.buildHeaders(extraHeaders)

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      let errorData: { error: ApiError }
      try {
        errorData = await response.json()
      } catch {
        errorData = {
          error: {
            code: 'UNKNOWN_ERROR',
            message: `Request failed with status ${response.status}`,
          },
        }
      }
      throw new ApiRequestError(errorData.error, response.status)
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { data: null as T }
    }

    const raw = await response.json()
    // Backend may return { success, message, data } or flat body (e.g. login { token, user })
    const data = raw != null && 'data' in raw && raw.data !== undefined ? raw.data : raw
    return { data, ...(raw?.pagination ? { pagination: raw.pagination } : {}) } as ApiResponse<T>
  }

  /**
   * Same request pipeline (base URL, auth token, per-request headers, error
   * envelope) but returning the **raw response text**, and accepting a raw
   * request body string.
   *
   * The Accounts console needs this: `JSON.parse` turns every JSON integer into
   * a `number`, which silently rounds above 2^53, so an amount must be read out
   * of the response text as digits (see `pages/accounts/bigintJson.ts`) and
   * written into the request body as a literal, never via a parse step that
   * would produce a `Number` first. No existing caller changes behaviour.
   */
  async requestText(
    method: string,
    path: string,
    options: {
      bodyText?: string
      params?: Record<string, string | number>
      headers?: Record<string, string>
    } = {}
  ): Promise<string> {
    const response = await fetch(this.buildUrl(path, options.params), {
      method,
      headers: this.buildHeaders(options.headers),
      body: options.bodyText,
    })

    const text = await response.text()
    if (!response.ok) {
      let error: ApiError
      try {
        // An error envelope carries no amount, so ordinary parsing is safe here.
        const parsed = JSON.parse(text) as { error?: ApiError | string }
        error =
          typeof parsed.error === 'string'
            ? { code: 'UNKNOWN_ERROR', message: parsed.error }
            : parsed.error ?? {
                code: 'UNKNOWN_ERROR',
                message: `Request failed with status ${response.status}`,
              }
      } catch {
        error = {
          code: 'UNKNOWN_ERROR',
          message: `Request failed with status ${response.status}`,
        }
      }
      throw new ApiRequestError(error, response.status)
    }
    return text
  }

  async get<T>(
    path: string,
    params?: Record<string, string | number>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, { params, headers })
  }

  async post<T>(
    path: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { body, headers })
  }

  async put<T>(
    path: string,
    body: unknown,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, { body, headers })
  }

  async delete<T>(path: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, { headers })
  }
}

export class ApiRequestError extends Error {
  public readonly error: ApiError
  public readonly status: number

  constructor(error: ApiError, status: number) {
    super(error.message)
    this.name = 'ApiRequestError'
    this.error = error
    this.status = status
  }
}

// Export singleton instance
export const api = new ApiClient()

// Export types for use in components
export type { ApiClient }
